import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import { Card } from 'primereact/card';
import _ from 'lodash';
import { Panel } from 'primereact/panel';
import { connect } from 'react-redux';
import { defineMessages, injectIntl } from 'react-intl';


const i18nMessages = defineMessages({
  headerSupLanguage: {
    id: 'attrs.support_lang.supportedLanguages',
    defaultMessage: 'Supported Languages',
  },
  headerQuestionnaire: {
    id: 'attrs.support_lang.questionnaire',
    defaultMessage: 'Questionnaire',
  },
  headerReport: {
    id: 'attrs.support_lang.report',
    defaultMessage: 'Report',
  },
  headerJPS: {
    id: 'attrs.support_lang.jps',
    defaultMessage: 'JPS',
  },
});

const AssessmentTempLanguages = ({
  formValues, behaviorSection, cognitiveSection, interestsSection, translatedTags, intl: { formatMessage },
}) => {
  const [asmtBehaviorDimensions] = useState(behaviorSection.dimensions);
  const [asmtDistortionDimensions] = useState(behaviorSection.distortion);
  const [asmtCognitiveDimensions] = useState(cognitiveSection.dimensions);
  const [asmtInterestsDimensions] = useState(interestsSection.dimensions);

  const [supportedQuestionnaireLangTags, setSupportedQuestionnaireLangTags] = useState([]);
  const [supportedReportLangTags, setSupportedReportLangTags] = useState([]);
  const [supportedJpsLangTags, setSupportedJpsLangTags] = useState([]);

  const init = useCallback(() => {
    const formBehaviourDimensionsNames = formValues.behavior ? formValues.behavior.map(dim => dim.name) : [];
    const formDistortionDimensionsNames = formValues.distortion ? formValues.distortion.map(dim => dim.name) : [];
    const formCognitiveDimensionsNames = formValues.cognitive ? formValues.cognitive.map(dim => dim.name) : [];
    const formInterestsDimensionsNames = formValues.interest ? formValues.interest.map(dim => dim.name) : [];

    let questLangTags = [];
    let reportLangTags = [];
    let jpsLangTags = [];

    formBehaviourDimensionsNames.forEach((formBehaviorDimName) => {
      const asmtBehaviorDim = asmtBehaviorDimensions.find(abd => abd.name === formBehaviorDimName);

      if (asmtBehaviorDim) {
        questLangTags = questLangTags.length === 0
          ? asmtBehaviorDim.questionnaireLanguages
          : _.intersection(questLangTags, asmtBehaviorDim.questionnaireLanguages);

        reportLangTags = reportLangTags.length === 0
          ? asmtBehaviorDim.reportLanguages
          : _.intersection(reportLangTags, asmtBehaviorDim.reportLanguages);

        jpsLangTags = jpsLangTags.length === 0
          ? asmtBehaviorDim.jpsLanguages
          : _.intersection(jpsLangTags, asmtBehaviorDim.jpsLanguages);
      }
    });

    formDistortionDimensionsNames.forEach((formDistortionDimName) => {
      const asmtDistortionDim = asmtDistortionDimensions.find(add => add.name === formDistortionDimName);
      if (asmtDistortionDim) {
        questLangTags = questLangTags.length === 0
          ? asmtDistortionDim.questionnaireLanguages
          : _.intersection(questLangTags, asmtDistortionDim.questionnaireLanguages);

        reportLangTags = reportLangTags.length === 0
          ? asmtDistortionDim.reportLanguages
          : _.intersection(reportLangTags, asmtDistortionDim.reportLanguages);

        jpsLangTags = jpsLangTags.length === 0
          ? asmtDistortionDim.jpsLanguages
          : _.intersection(jpsLangTags, asmtDistortionDim.jpsLanguages);
      }
    });

    formCognitiveDimensionsNames.forEach((formCognitiveDimName) => {
      const asmtCognitiveDim = asmtCognitiveDimensions.find(acd => acd.name === formCognitiveDimName);

      questLangTags = questLangTags.length === 0
        ? asmtCognitiveDim.questionnaireLanguages
        : _.intersection(questLangTags, asmtCognitiveDim.questionnaireLanguages);

      reportLangTags = reportLangTags.length === 0
        ? asmtCognitiveDim.reportLanguages
        : _.intersection(reportLangTags, asmtCognitiveDim.reportLanguages);

      jpsLangTags = jpsLangTags.length === 0
        ? asmtCognitiveDim.jpsLanguages
        : _.intersection(jpsLangTags, asmtCognitiveDim.jpsLanguages);
    });

    formInterestsDimensionsNames.forEach((formInterestDimName) => {
      const asmtInterestDim = asmtInterestsDimensions.find(aid => aid.name === formInterestDimName);

      questLangTags = questLangTags.length === 0
        ? asmtInterestDim.questionnaireLanguages
        : _.intersection(questLangTags, asmtInterestDim.questionnaireLanguages);

      reportLangTags = reportLangTags.length === 0
        ? asmtInterestDim.reportLanguages
        : _.intersection(reportLangTags, asmtInterestDim.reportLanguages);

      jpsLangTags = jpsLangTags.length === 0
        ? asmtInterestDim.jpsLanguages
        : _.intersection(jpsLangTags, asmtInterestDim.jpsLanguages);
    });

    setSupportedQuestionnaireLangTags(questLangTags);
    setSupportedReportLangTags(reportLangTags);
    setSupportedJpsLangTags(jpsLangTags);
  }, [
    formValues,
    asmtBehaviorDimensions, asmtDistortionDimensions, asmtCognitiveDimensions, asmtInterestsDimensions,
  ]);

  useEffect(() => {
    init();
  }, [init]);

  const getTranslatedTag = (tag) => {
    const foundTranslatedTag = translatedTags.find(tt => tt.value === tag);
    return foundTranslatedTag ? foundTranslatedTag.label : tag;
  };

  const renderSupportedLanguagesPanels = () => (
    <>
      <Card
        subTitle={formatMessage(i18nMessages.headerQuestionnaire)}
        style={{ border: '1px solid lightgrey', marginBottom: '5px' }}
      >
        <ul style={{ listStyle: 'inside none none' }}>
          {
            supportedQuestionnaireLangTags.map(sql => (
              <li key={sql} style={{ fontStyle: 'italic' }}>
                <i className="fa fa-check icon-success" aria-hidden="true" />
                {` ${getTranslatedTag(sql)}`}
              </li>
            ))
          }
        </ul>
      </Card>

      <Card
        subTitle={formatMessage(i18nMessages.headerReport)}
        style={{ border: '1px solid lightgrey', marginBottom: '5px' }}
      >
        <ul style={{ listStyle: 'inside none none' }}>
          {
            supportedReportLangTags.map(srl => (
              <li key={srl} style={{ fontStyle: 'italic' }}>
                <i className="fa fa-check icon-success" aria-hidden="true" />
                {` ${getTranslatedTag(srl)}`}
              </li>
            ))
          }
        </ul>
      </Card>

      <Card
        subTitle={formatMessage(i18nMessages.headerJPS)}
        style={{ border: '1px solid lightgrey' }}
      >
        <ul style={{ listStyle: 'inside none none' }}>
          {
            supportedJpsLangTags.map(sjl => (
              <li key={sjl} style={{ fontStyle: 'italic' }}>
                <i className="fa fa-check icon-success" aria-hidden="true" />
                {` ${getTranslatedTag(sjl)}`}
              </li>
            ))
          }
        </ul>
      </Card>
    </>
  );

  return (
    <div>
      <div className="p-grid">
        <div className="p-col-12">
          <Panel header={formatMessage(i18nMessages.headerSupLanguage)}>
            {renderSupportedLanguagesPanels()}
          </Panel>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const { translatedTags } = state.i18n;
  return {
    translatedTags,
  };
};

AssessmentTempLanguages.propTypes = {
  formValues: PropTypes.objectOf(PropTypes.any).isRequired,
  behaviorSection: PropTypes.objectOf(PropTypes.any).isRequired,
  cognitiveSection: PropTypes.objectOf(PropTypes.any).isRequired,
  interestsSection: PropTypes.objectOf(PropTypes.any).isRequired,
  translatedTags: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  intl: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default connect(mapStateToProps)(injectIntl(AssessmentTempLanguages));
